/**
 * Supabase Education Repository
 *
 * Implements IEducationRepository for Supabase storage.
 * Handles lesson progress CRUD, aggregated progress calculations,
 * prerequisite checking, and real-time subscriptions.
 *
 * @module repositories/supabase/SupabaseEducationRepository
 */
import { getSupabaseClient } from '../../services/auth/supabase';
import type { IEducationRepository, Unsubscribe } from '../interfaces/IEducationRepository';
import type {
  LessonProgress,
  ModuleProgress,
  PathwayProgress,
  MarkLessonCompleteInput,
  UpdateLessonProgressInput,
  Pathway,
  Lesson,
  Module,
  PrerequisiteStatus,
  LessonStatus,
} from '../../types/education';
import type { Database } from '../../types/database.types';
import type { RealtimeChannel } from '@supabase/supabase-js';
import { clientLogger } from '@/lib/clientLogger';

type LessonProgressRow = Database['public']['Tables']['lesson_progress']['Row'];
type LessonProgressInsert = Database['public']['Tables']['lesson_progress']['Insert'];
type LessonProgressDbUpdate = Database['public']['Tables']['lesson_progress']['Update'];

// =============================================================================
// Row Conversion Helpers
// =============================================================================

/**
 * Convert database row to LessonProgress domain object.
 */
function rowToLessonProgress(row: LessonProgressRow): LessonProgress {
  return {
    lessonId: row.lesson_id,
    moduleId: row.module_id,
    pathwayId: row.pathway_id,
    status: row.status as LessonStatus,
    startedAt: row.created_at,
    completedAt: row.completed_at ?? undefined,
    percentComplete: row.progress ?? 0,
    lastPosition: undefined, // Not stored in DB
    notes: undefined, // Not stored in DB
  };
}

/**
 * Create a default LessonProgress for lessons not yet started.
 */
function createDefaultLessonProgress(
  lessonId: string,
  moduleId: string,
  pathwayId: string
): LessonProgress {
  return {
    lessonId,
    moduleId,
    pathwayId,
    status: 'not_started',
    percentComplete: 0,
  };
}

// =============================================================================
// Repository Implementation
// =============================================================================

export class SupabaseEducationRepository implements IEducationRepository {
  // ===========================================================================
  // Lesson Progress Operations
  // ===========================================================================

  async getLessonProgress(userId: string, lessonId: string): Promise<LessonProgress | null> {
    const supabase = getSupabaseClient();
    if (!supabase) throw new Error('Supabase not configured');

    const { data, error } = await supabase
      .from('lesson_progress')
      .select('*')
      .eq('user_id', userId)
      .eq('lesson_id', lessonId)
      .maybeSingle();

    if (error) {
      throw new Error(`Failed to fetch lesson progress: ${error.message}`);
    }
    if (!data) return null;

    return rowToLessonProgress(data);
  }

  async getAllProgress(userId: string): Promise<LessonProgress[]> {
    const supabase = getSupabaseClient();
    if (!supabase) throw new Error('Supabase not configured');

    const { data, error } = await supabase
      .from('lesson_progress')
      .select('*')
      .eq('user_id', userId)
      .order('updated_at', { ascending: false });

    if (error) throw new Error(`Failed to fetch all progress: ${error.message}`);
    return data.map(rowToLessonProgress);
  }

  async getPathwayLessonProgress(userId: string, pathwayId: string): Promise<LessonProgress[]> {
    const supabase = getSupabaseClient();
    if (!supabase) throw new Error('Supabase not configured');

    const { data, error } = await supabase
      .from('lesson_progress')
      .select('*')
      .eq('user_id', userId)
      .eq('pathway_id', pathwayId)
      .order('updated_at', { ascending: false });

    if (error) throw new Error(`Failed to fetch pathway progress: ${error.message}`);
    return data.map(rowToLessonProgress);
  }

  async getModuleLessonProgress(userId: string, moduleId: string): Promise<LessonProgress[]> {
    const supabase = getSupabaseClient();
    if (!supabase) throw new Error('Supabase not configured');

    const { data, error } = await supabase
      .from('lesson_progress')
      .select('*')
      .eq('user_id', userId)
      .eq('module_id', moduleId)
      .order('updated_at', { ascending: false });

    if (error) throw new Error(`Failed to fetch module progress: ${error.message}`);
    return data.map(rowToLessonProgress);
  }

  async markLessonComplete(
    userId: string,
    input: MarkLessonCompleteInput
  ): Promise<LessonProgress> {
    const supabase = getSupabaseClient();
    if (!supabase) throw new Error('Supabase not configured');

    const now = new Date().toISOString();

    const upsertData: LessonProgressInsert = {
      user_id: userId,
      lesson_id: input.lessonId,
      module_id: input.moduleId,
      pathway_id: input.pathwayId,
      status: 'completed',
      progress: 100,
      completed_at: now,
    };

    const { data, error } = await supabase
      .from('lesson_progress')
      .upsert(upsertData, { onConflict: 'user_id,lesson_id' })
      .select()
      .single();

    if (error) throw new Error(`Failed to mark lesson complete: ${error.message}`);
    return rowToLessonProgress(data);
  }

  async updateLessonProgress(
    userId: string,
    input: UpdateLessonProgressInput
  ): Promise<LessonProgress> {
    const supabase = getSupabaseClient();
    if (!supabase) throw new Error('Supabase not configured');

    const status: LessonStatus =
      input.percentComplete >= 100 ? 'completed' : input.percentComplete > 0 ? 'in_progress' : 'not_started';

    const now = new Date().toISOString();

    const upsertData: LessonProgressInsert = {
      user_id: userId,
      lesson_id: input.lessonId,
      module_id: input.moduleId,
      pathway_id: input.pathwayId,
      status,
      progress: input.percentComplete,
      completed_at: status === 'completed' ? now : null,
    };

    const { data, error } = await supabase
      .from('lesson_progress')
      .upsert(upsertData, { onConflict: 'user_id,lesson_id' })
      .select()
      .single();

    if (error) throw new Error(`Failed to update lesson progress: ${error.message}`);
    return rowToLessonProgress(data);
  }

  async resetLessonProgress(userId: string, lessonId: string): Promise<void> {
    const supabase = getSupabaseClient();
    if (!supabase) throw new Error('Supabase not configured');

    const { error } = await supabase
      .from('lesson_progress')
      .delete()
      .eq('user_id', userId)
      .eq('lesson_id', lessonId);

    if (error) throw new Error(`Failed to reset lesson progress: ${error.message}`);
  }

  async resetPathwayProgress(userId: string, pathwayId: string): Promise<void> {
    const supabase = getSupabaseClient();
    if (!supabase) throw new Error('Supabase not configured');

    const { error } = await supabase
      .from('lesson_progress')
      .delete()
      .eq('user_id', userId)
      .eq('pathway_id', pathwayId);

    if (error) throw new Error(`Failed to reset pathway progress: ${error.message}`);
  }

  async resetAllProgress(userId: string): Promise<void> {
    const supabase = getSupabaseClient();
    if (!supabase) throw new Error('Supabase not configured');

    const { error } = await supabase
      .from('lesson_progress')
      .delete()
      .eq('user_id', userId);

    if (error) throw new Error(`Failed to reset all progress: ${error.message}`);
  }

  // ===========================================================================
  // Aggregated Progress Calculations
  // ===========================================================================

  calculateModuleProgress(
    pathwayId: string,
    moduleId: string,
    totalLessons: number,
    lessonProgress: LessonProgress[]
  ): ModuleProgress {
    const completedLessons = lessonProgress.filter(
      (lp) => lp.moduleId === moduleId && lp.status === 'completed'
    ).length;

    const percentComplete = totalLessons > 0 ? Math.round((completedLessons / totalLessons) * 100) : 0;
    const isComplete = completedLessons === totalLessons && totalLessons > 0;

    // Find the latest completion time
    const completedAt = isComplete
      ? lessonProgress
          .filter((lp) => lp.moduleId === moduleId && lp.completedAt)
          .sort((a, b) => (b.completedAt ?? '').localeCompare(a.completedAt ?? ''))[0]?.completedAt
      : undefined;

    return {
      moduleId,
      pathwayId,
      lessonsCompleted: completedLessons,
      totalLessons,
      percentComplete,
      isComplete,
      completedAt,
    };
  }

  calculatePathwayProgress(pathway: Pathway, lessonProgress: LessonProgress[]): PathwayProgress {
    const totalLessons = pathway.modules.reduce((sum, m) => sum + m.lessons.length, 0);
    const completedLessons = lessonProgress.filter((lp) => lp.status === 'completed').length;

    // Calculate module completions
    let modulesCompleted = 0;
    for (const module of pathway.modules) {
      const moduleProgress = this.calculateModuleProgress(
        pathway.id,
        module.id,
        module.lessons.length,
        lessonProgress
      );
      if (moduleProgress.isComplete) {
        modulesCompleted++;
      }
    }

    const percentComplete = totalLessons > 0 ? Math.round((completedLessons / totalLessons) * 100) : 0;
    const isComplete = completedLessons === totalLessons && totalLessons > 0;

    // Find the latest completion time
    const completedAt = isComplete
      ? lessonProgress
          .filter((lp) => lp.completedAt)
          .sort((a, b) => (b.completedAt ?? '').localeCompare(a.completedAt ?? ''))[0]?.completedAt
      : undefined;

    // Find the last accessed time
    const lastAccessedAt = lessonProgress
      .filter((lp) => lp.startedAt)
      .sort((a, b) => (b.startedAt ?? '').localeCompare(a.startedAt ?? ''))[0]?.startedAt;

    return {
      pathwayId: pathway.id,
      modulesCompleted,
      totalModules: pathway.modules.length,
      lessonsCompleted: completedLessons,
      totalLessons,
      percentComplete,
      isComplete,
      completedAt,
      lastAccessedAt,
    };
  }

  async getPathwayProgress(userId: string, pathway: Pathway): Promise<PathwayProgress> {
    const lessonProgress = await this.getPathwayLessonProgress(userId, pathway.id);
    return this.calculatePathwayProgress(pathway, lessonProgress);
  }

  async getModuleProgress(
    userId: string,
    pathwayId: string,
    moduleId: string,
    totalLessons: number
  ): Promise<ModuleProgress> {
    const lessonProgress = await this.getModuleLessonProgress(userId, moduleId);
    return this.calculateModuleProgress(pathwayId, moduleId, totalLessons, lessonProgress);
  }

  // ===========================================================================
  // Prerequisite Checking
  // ===========================================================================

  async checkLessonPrerequisites(userId: string, lesson: Lesson): Promise<PrerequisiteStatus> {
    // If no prerequisites, lesson is unlocked
    if (!lesson.prerequisites || lesson.prerequisites.length === 0) {
      return {
        isUnlocked: true,
        completedPrerequisites: [],
        missingPrerequisites: [],
      };
    }

    const allProgress = await this.getAllProgress(userId);
    const completedLessonIds = new Set(
      allProgress.filter((lp) => lp.status === 'completed').map((lp) => lp.lessonId)
    );

    const completedPrerequisites: string[] = [];
    const missingPrerequisites: string[] = [];

    for (const prereqId of lesson.prerequisites) {
      if (completedLessonIds.has(prereqId)) {
        completedPrerequisites.push(prereqId);
      } else {
        missingPrerequisites.push(prereqId);
      }
    }

    return {
      isUnlocked: missingPrerequisites.length === 0,
      completedPrerequisites,
      missingPrerequisites,
    };
  }

  async checkModulePrerequisites(
    userId: string,
    module: Module,
    pathwayId: string
  ): Promise<PrerequisiteStatus> {
    // If no prerequisites, module is unlocked
    if (!module.prerequisites || module.prerequisites.length === 0) {
      return {
        isUnlocked: true,
        completedPrerequisites: [],
        missingPrerequisites: [],
      };
    }

    // Get all progress for the pathway to check module completion
    const pathwayProgress = await this.getPathwayLessonProgress(userId, pathwayId);

    // Group progress by module
    const moduleProgressMap = new Map<string, LessonProgress[]>();
    for (const lp of pathwayProgress) {
      const existing = moduleProgressMap.get(lp.moduleId) ?? [];
      existing.push(lp);
      moduleProgressMap.set(lp.moduleId, existing);
    }

    const completedPrerequisites: string[] = [];
    const missingPrerequisites: string[] = [];

    // Note: We can't determine total lessons per module without the pathway definition
    // For now, we check if any lessons in the prerequisite module are completed
    // A more robust implementation would require passing the full pathway
    for (const prereqModuleId of module.prerequisites) {
      const moduleProgress = moduleProgressMap.get(prereqModuleId) ?? [];
      const completedInModule = moduleProgress.filter((lp) => lp.status === 'completed').length;

      // Consider a module "complete enough" if at least one lesson is completed
      // In practice, the full pathway definition would be used to check all lessons
      if (completedInModule > 0) {
        completedPrerequisites.push(prereqModuleId);
      } else {
        missingPrerequisites.push(prereqModuleId);
      }
    }

    return {
      isUnlocked: missingPrerequisites.length === 0,
      completedPrerequisites,
      missingPrerequisites,
    };
  }

  async isLessonUnlocked(userId: string, lesson: Lesson): Promise<boolean> {
    const status = await this.checkLessonPrerequisites(userId, lesson);
    return status.isUnlocked;
  }

  async isModuleUnlocked(userId: string, module: Module, pathwayId: string): Promise<boolean> {
    const status = await this.checkModulePrerequisites(userId, module, pathwayId);
    return status.isUnlocked;
  }

  // ===========================================================================
  // Real-time Subscriptions
  // ===========================================================================

  subscribe(userId: string, callback: (progress: LessonProgress[]) => void): Unsubscribe {
    const supabase = getSupabaseClient();
    if (!supabase) {
      clientLogger.warn('[SupabaseEducationRepository] Cannot subscribe: Supabase not configured');
      callback([]);
      return () => {};
    }

    let channel: RealtimeChannel | null = null;

    // Initial fetch
    this.getAllProgress(userId)
      .then(callback)
      .catch((err) => {
        clientLogger.error('[SupabaseEducationRepository] Initial fetch failed:', err);
        callback([]);
      });

    // Set up real-time subscription
    channel = supabase
      .channel(`lesson_progress:${userId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'lesson_progress',
          filter: `user_id=eq.${userId}`,
        },
        () => {
          // Re-fetch all progress on any change
          this.getAllProgress(userId)
            .then(callback)
            .catch((err) => clientLogger.error('[SupabaseEducationRepository] Refetch failed:', err));
        }
      )
      .subscribe();

    return () => {
      if (channel) {
        supabase.removeChannel(channel);
      }
    };
  }

  subscribeToPathway(
    userId: string,
    pathwayId: string,
    callback: (progress: LessonProgress[]) => void
  ): Unsubscribe {
    const supabase = getSupabaseClient();
    if (!supabase) {
      clientLogger.warn('[SupabaseEducationRepository] Cannot subscribe: Supabase not configured');
      callback([]);
      return () => {};
    }

    let channel: RealtimeChannel | null = null;

    // Initial fetch
    this.getPathwayLessonProgress(userId, pathwayId)
      .then(callback)
      .catch((err) => {
        clientLogger.error('[SupabaseEducationRepository] Initial fetch failed:', err);
        callback([]);
      });

    // Set up real-time subscription
    channel = supabase
      .channel(`lesson_progress:${userId}:${pathwayId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'lesson_progress',
          filter: `user_id=eq.${userId}`,
        },
        () => {
          // Re-fetch pathway progress on any change
          this.getPathwayLessonProgress(userId, pathwayId)
            .then(callback)
            .catch((err) => clientLogger.error('[SupabaseEducationRepository] Refetch failed:', err));
        }
      )
      .subscribe();

    return () => {
      if (channel) {
        supabase.removeChannel(channel);
      }
    };
  }
}
